<template>
    <div
        v-if="visible"
        ref="chart"
        class="hts-gantt">
        <div
            class="relative rounded border-none"
            :style="{ width: innerWidth + 'px', height: innerHeight + 'px' }">
            <div
                ref="ganttHeader"
                class="hts-gantt__rows border bg-gray-50 z-20 relative inline-block align-middle overflow-hidden"
                :style="{ width: rowsStats.width + 'px', height: rowsStats.height + 'px' }">
                <div class="hts-gantt__rows_content">
                    <div
                        class="hts-gantt__rows_header z-20 relative flex items-center border-b"
                        :style="{ width: rowsHeaderStats.width + 'px', height: rowsHeaderStats.height - 1 + 'px' }">
                        <slot name="rows-header">
                            <div class="ml-2">
                                {{ label }}
                            </div>
                        </slot>
                    </div>
                    <div
                        ref="headerContent"
                        class="hts-gantt__rows_data relative z-10"
                        :style="{ width: rowsContentStats.width + 'px', height: rowsContentStats.height * 10 + 'px' }">
                        <div :style="{ width: '1px', height: layoutHeight + 'px' }" />
                        <template
                            v-for="(row, rIndex) in filteredRows"
                            :key="row.gId">
                            <div
                                style="position: absolute"
                                :style="row.style">
                                <div
                                    class="hts-gantt__rows_item flex items-center px-2"
                                    @click="handleRowItemClick(row, rIndex, $event)">
                                    <input
                                        v-if="selectionVisible"
                                        :type="selectionType"
                                        name="row-group"
                                        @change="onChange(row, $event)" >
                                    <slot
                                        name="row-item"
                                        :row="row">
                                        <span class="whitespace-nowrap ml-1">
                                            {{ row.code }}
                                        </span>
                                    </slot>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div
                ref="ganttGrid"
                class="hts-gantt__grid z-10 relative inline-block align-middle border-t border-b border-r"
                :style="{ width: gridStats.width + 'px', height: gridStats.height + 'px' }">
                <div
                    ref="gridContent"
                    class="hts-gantt__grid_content overflow-hidden"
                    style="width: inherit; height: inherit">
                    <div
                        class="hts-gantt__grid_header z-20 relative select-none bg-gray-50"
                        :style="{ width: layoutWidth + 'px', height: gridHeaderStats.height + 'px' }">
                        <div
                            class="gantt_header_line"
                            style="height: 22px; position: relative; line-height: 22px">
                            <template
                                v-for="(header1, hi) in headerLines1"
                                :key="hi">
                                <div
                                    class="gantt_header_cell week border-r border-b text-center"
                                    :aria-label="header1.label"
                                    style="position: absolute"
                                    :style="header1.style">
                                    {{ header1.label }}
                                </div>
                            </template>
                        </div>
                        <div
                            class="gantt_header_line"
                            style="height: 22px; position: relative; line-height: 22px">
                            <template
                                v-for="(header2, hi2) in headerLines2"
                                :key="hi2">
                                <div
                                    v-if="checkHeaderVisible(header2, hi2)"
                                    class="gantt_header_cell week border-r border-b text-center"
                                    :aria-label="header2.label"
                                    style="position: absolute"
                                    :style="header2.style">
                                    {{ header2.label }}
                                </div>
                            </template>
                        </div>
                    </div>
                    <div
                        class="hts-gantt__grid_data z-10 relative"
                        :style="{ width: layoutWidth + 'px', height: gridContentStats.height + 'px' }">
                        <div v-if="cPreviewStyle">
                            <hts-tooltip>
                                <template #reference>
                                    <div
                                        class="h-full absolute bg-blue-300 opacity-50"
                                        :style="{ left: `${cPreviewStyle.left * 100}%`, width: `${cPreviewStyle.width * 100}%` }" />
                                </template>
                            </hts-tooltip>
                        </div>

                        <div
                            class="hts-gantt__grid_data_bg"
                            style="height: 2800px"
                            :style="{ width: layoutWidth + 'px' }">
                            <template
                                v-for="(row, rIndex) in filteredRows"
                                :key="row.gId">
                                <div
                                    v-if="row.style"
                                    class="hts-gantt__grid_data_bg_item"
                                    style="position: absolute; left: 0px"
                                    :style="{ top: row.style.top, height: row.style.height, width: layoutWidth + 'px' }" />
                            </template>
                        </div>

                        <div
                            ref="chartRows"
                            class="hts-gantt__bars">
                            <template
                                v-for="(item, iIndex) in filteredItems"
                                :key="item.gId">
                                <div
                                    style="position: absolute; margin-top: 3px"
                                    class="select-none overflow-visible"
                                    :style="item.style"
                                    @click="onBarClick(item, iIndex, $event)">
                                    <slot
                                        name="bar-item"
                                        :item="item">
                                        <hts-tooltip class="block w-full h-full bg-green-300 border rounded whitespace-nowrap overflow-hidden text-ellipsis px-1">
                                            <template #reference>
                                                <span>
                                                    {{ item.code }}
                                                </span>
                                            </template>
                                            <slot
                                                name="bar-tooltip"
                                                :item="item">
                                                <span class="text-lg font-bold">
                                                    {{ item.code }}
                                                </span>
                                                <hts-stage-names :items="item.stageNames" />
                                                <div>
                                                    <span class="mt-2 mr-2 text-green-600 px-2 border rounded whitespace-nowrap inline-block align-middle">
                                                        {{ $formatters.datetime(item.timeIn) }}
                                                    </span>
                                                    <span class="mt-2 text-accent-600 px-2 border rounded whitespace-nowrap inline-block align-middle">
                                                        {{ $formatters.datetime(item.timeOut) }}
                                                    </span>
                                                </div>
                                            </slot>
                                        </hts-tooltip>
                                    </slot>
                                    <template v-if="false">
                                        <div
                                            class="absolute"
                                            style="top: 3px; height: 30px">
                                            <span>
                                                {{ item.code }}
                                            </span>
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div
                ref="hScroll"
                class="hts-gantt__h-scroll absolute bottom-0 left-0 overflow-auto z-auto"
                :class="{ invisible: !scrollH.visible }"
                :style="{ width: scrollH.width + 'px', height: scrollH.height + 'px' }"
                @scroll="onScroll('h', $event)">
                <div
                    class="h-full z-auto"
                    :style="{ width: scrollH.width * (layoutWidth / (gridStats.width || 1)) + 'px', minHeight: scrollH.height + 'px' }" />
            </div>

            <div
                ref="vScroll"
                class="hts-gantt__v-scroll absolute right-0 overflow-auto z-auto"
                :class="{ invisible: !scrollV.visible }"
                :style="{ top: gridHeaderStats.height + 'px', width: scrollV.width + 'px', height: scrollV.height + 'px' }"
                @scroll="onScroll('v', $event)">
                <div
                    class="scroller w-full z-auto"
                    :style="{ height: scrollV.height * (layoutHeight / (gridContentStats.height || 1)) + 'px', minWidth: scrollV.width + 'px' }" />
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs'

const { $dayjs, $formatters }: any = useNuxtApp()
const props = defineProps({
  label: String,
  from: String,
  to: String,
  columns: {
    type: Array,
    default: () => []
  },
  rows: {
    type: Array,
    default: () => []
  },
  items: {
    type: Array,
    default: () => []
  },
  selectionType: {
    type: String,
    default: 'radio'
  },
  utcOffset: {
    type: Number,
    default: 0
  },
  scaleWidth: {
    type: Number as PropType<number>,
    default: 1
  },
  fromReview: String,
  toReview: String
})

const emits = defineEmits(['bar-click', 'row-click', 'check', 'selection-change'])
const { from, to, columns, rows, items, selectionType, scaleWidth, fromReview, toReview } = toRefs(props)
const datetime = (s?: string) => {
  return $dayjs(s)
}
const __el: any = ref(null)
const headerContent: any = ref()
const gridContent: any = ref()
const hScroll: any = ref()
const vScroll: any = ref()
const scale = ref('month') // week-day; day-hour

const fromDate = computed(() => {
  if (from?.value) {
    return datetime(from?.value).startOf('w').startOf('D')
  }
  return datetime().subtract(datetime().utcOffset(), 'm').subtract(15, 'day').startOf('w').startOf('D')
})
const toDate = computed(() => {
  if (to?.value) {
    return datetime(to?.value).endOf('w').endOf('D')
  }
  return datetime().subtract(datetime().utcOffset(), 'm').add(15, 'day').endOf('w').endOf('D')
  // return fromDate.value.add(4 * 7 - 1, 'd').endOf('D')
})

const CProgressRatio = computed(() => {
  if (from?.value || to?.value) {
    return 0
  }

  const totalDays = dayjs(toDate.value).diff(fromDate.value, 'day') || 1 // Tổng số ngày
  const currentDay = dayjs().subtract(2, 'day') // Ngày hiện tại trừ 2 ngày
  const elapsedDays = currentDay.diff(fromDate.value, 'day') || 1 // Số ngày đã trôi qua

  const progressRatio = elapsedDays / totalDays // Tỷ lệ thời gian đã trôi qua

  return progressRatio
})

const chart: any = ref(null)
const selectionVisible = ref(false)
const selected = ref('')
const innerWidth = ref<number>(0)
const innerHeight = ref<number>(0)
const rowsStats = reactive({
  width: 100,
  height: 100
})
const rowsHeaderStats = reactive({
  width: 100,
  height: 45
})
const rowsContentStats = reactive({
  width: 100,
  height: 100
})
const gridStats = reactive({
  width: 100,
  height: 45,
  cellHeight: 36,
  cellWidth: 24
})
const gridHeaderStats = reactive({
  width: 100,
  height: 45
})
const gridContentStats = reactive({
  width: 100,
  height: 45
})
const scrollV = reactive({
  visible: false,
  width: 20, // fixed
  height: 100,
  top: 0
})
const scrollH = reactive({
  visible: false,
  width: 100,
  height: 20, // fixed
  left: 0
})
const maxRowCount = computed(() => {
  return parseInt((rowsContentStats.height / gridStats.cellHeight).toString())
})
const maxCellCount = computed(() => {
  return parseInt((gridStats.width / gridStats.cellWidth).toString())
})

const visible = ref<boolean>(true)
watch(
  () => scaleWidth.value,
  (val: number) => {
    visible.value = false
    gridStats.cellWidth = Math.floor(24 * val)
    nextTick(() => {
      visible.value = true
    })
  }
)

const currentWeek = ref(0)
const currentDay = ref(0)

watch(
  () => from?.value,
  () => updateViewport(scale.value)
)
watch(
  () => to?.value,
  () => updateViewport(scale.value)
)

watch(
  () => scale.value,
  (value) => updateViewport(value)
)

const cPreviewStyle = computed(() => {
  if (!fromReview?.value || !toReview?.value) {
    return null
  }

  const fromDateNumber = new Date(fromDate?.value).valueOf()
  const toDateNumber = new Date(toDate?.value).valueOf()

  const fromReviewNumber = new Date(fromReview?.value).valueOf()
  const toReviewNumber = new Date(toReview?.value).valueOf()

  if (!fromDateNumber || !toDateNumber) {
    return null
  }

  if (!fromReviewNumber || !toReviewNumber) {
    return null
  }

  if (toDateNumber <= fromDateNumber) {
    return null
  }

  if (toReviewNumber <= fromReviewNumber) {
    return null
  }

  const totalTime = toDateNumber - fromDateNumber
  const leftTime = fromReviewNumber - fromDateNumber
  const previewTime = toReviewNumber - fromReviewNumber

  const left = leftTime / totalTime
  const width = previewTime / totalTime
  return {
    left,
    width,
    fromReview: fromDateNumber,
    toReview: toReviewNumber
  }
})

function updateViewport (type: string) {
  switch (type) {
  case 'week-day':
    viewport.from = $dayjs().week(currentWeek.value).startOf('w').startOf('d')
    viewport.to = $dayjs().week(currentWeek.value).endOf('w').endOf('d')
    break
  case 'day-hour':
    viewport.from = $dayjs().day(currentDay.value).startOf('d')
    viewport.to = $dayjs().day(currentDay.value).endOf('d')
    break
  default:
    viewport.from = fromDate.value
    viewport.to = toDate.value
  }

  nextTick(() => {
    refresh()
  })
}

const headerLines1 = computed<any[]>(() => {
  const result = []
  for (let i = 0; i < currentOffsetDays.value / 7; i++) {
    let add = i * 7
    if (add > currentOffsetDays.value) {
      add = currentOffsetDays.value - i
    }
    const tmp = fromDate.value.add(add, 'd')

    result.push({
      label: tmp.format('DD MMM') + ' - ' + tmp.add(6, 'd').format('DD MMM'),
      week: tmp.add(6, 'd').week(),
      style: {
        left: add * gridStats.cellWidth + 'px',
        width: gridStats.cellWidth * 7 + 'px',
        height: 22 + 'px'
      }
    })
  }
  return result
})
const headerLines2 = computed<any[]>(() => {
  const result = []
  for (let i = 0; i < currentOffsetDays.value; i++) {
    const tmp = fromDate.value.add(i, 'd')
    result.push({
      label: tmp.format('D'),
      day: tmp.day(),
      style: {
        left: i * gridStats.cellWidth + 'px',
        width: gridStats.cellWidth + 'px',
        height: 22 + 'px'
      }
    })
  }
  return result
})
const currentOffsetDays = computed<number>(() => {
  return toDate.value.diff(fromDate.value, 'd') + 1
})
const viewport = reactive({
  from: fromDate.value.valueOf(),
  to: toDate.value.valueOf(),
  rowIndex: 0
})
const layoutWidth = computed(() => {
  return currentOffsetDays.value * gridStats.cellWidth
})
const layoutHeight = computed(() => {
  return rows.value.length * gridStats.cellHeight + (scrollH.visible ? scrollH.height : 0)
})
const cRows = ref<any[]>([])
const cItems = ref<any[]>([])
const rowOffset = ref<number>(0)
const filteredRows = computed(() => {
  return cRows.value
    .filter((c: any, i: number) => {
      return i > viewport.rowIndex - 1 && i < viewport.rowIndex + maxRowCount.value + 1
    })
    .map((r: any, i: number) => {
      if (!r.style) {
        r.style = {
          width: rowsStats.width + 'px',
          height: gridStats.cellHeight + 'px',
          lineHeight: gridStats.cellHeight + 'px',
          left: 0
        }
      }
      r.style.top = i * gridStats.cellHeight + rowOffset.value + 'px'
      return r
    })
})
const filteredItems = computed(() => {
  return cItems.value
    .filter((c: any) => {
      return rowIds.value.includes(c.rowId) && c.timeIn >= viewport.from && c.timeOut <= viewport.to
    })
    .map((item: any, i: number) => {
      const row = filteredRows.value.find((r: any) => r.id === item.rowId)
      if (!row) {
        return item
      }
      if (!item.style) {
        const left = Math.ceil(datetime(item.timeIn).diff(fromDate.value, 'm') / 60) * (gridStats.cellWidth / 24)
        const width = Math.ceil(datetime(item.timeOut).diff(datetime(item.timeIn), 'm') / 60) * (gridStats.cellWidth / 24)
        item.style = {
          width: width + 'px',
          height: gridStats.cellHeight - 6 + 'px',
          lineHeight: gridStats.cellHeight - 6 + 'px',
          left: left + 'px'
        }
      }
      item.style.top = row.style.top
      return item
    })
})

const rowIds = computed<any[]>(() => {
  return filteredRows.value.map((r: any) => r.id)
})

const setCRows = () => {
  cRows.value = rows.value.map((r: any, i: number) => {
    if (!r.id) {
      r.id = i.toString()
    }
    Object.assign(r, {
      style: {
        width: rowsStats.width + 'px',
        height: gridStats.cellHeight + 'px',
        lineHeight: gridStats.cellHeight + 'px',
        left: 0
      }
    })
    return r
  })
}

const setCItems = () => {
  cItems.value = items.value.map((item: any, i: number) => {
    if (!item.id) {
      item.id = item._id || i.toString()
    }
    const left = datetime(item.timeIn).diff(fromDate.value, 'h') * (gridStats.cellWidth / 24)
    const width = datetime(item.timeOut).diff(datetime(item.timeIn), 'h') * (gridStats.cellWidth / 24)
    Object.assign(item, {
      width,
      style: {
        width: width + 'px',
        height: gridStats.cellHeight - 6 + 'px',
        lineHeight: gridStats.cellHeight - 6 + 'px',
        left: left + 'px'
      }
    })
    return item
  })
}
const setCRowsDebounce = $lodash.debounce(setCRows, 500)
const setCItemsDebounce = $lodash.debounce(setCItems, 500)
const ganttHeader: any = ref(null)
const ganttGrid: any = ref(null)
onMounted(async () => {
  gridStats.cellWidth = scaleWidth.value * 24
  __el.value = getCurrentInstance()?.proxy?.$el

  await setComponentSize()

  window.addEventListener('resize', onResize)
  ganttHeader.value && ganttHeader.value.addEventListener('mousewheel', rerouteScroll, false)
  ganttGrid.value && ganttGrid.value.addEventListener('mousewheel', rerouteScroll, false)
  nextTick(() => {
    setCRows()
    nextTick(() => {
      setCItems()

      hScroll.value.scrollLeft = hScroll.value.scrollWidth * CProgressRatio.value
    })
  })
})

onBeforeUnmount(() => {
  window?.removeEventListener('resize', onResize)
  ganttHeader?.value && ganttHeader.value.removeEventListener('mousewheel', rerouteScroll)
  ganttGrid?.value && ganttGrid.value.removeEventListener('mousewheel', rerouteScroll)
})

const refresh = () => {
  setCRows()
  nextTick(() => {
    setCItems()
    hScroll.value.scrollLeft = hScroll.value.scrollWidth * CProgressRatio.value
  })
}
const refreshRows = () => {
  setCRowsDebounce()
}
const refreshItems = () => {
  setCItemsDebounce()
}

const onResize = (e: any) => {
  setComponentSizeDebounce()
}

const setComponentSize = async () => {
  await new Promise((resolve) => {
    innerWidth.value = 0
    innerHeight.value = 0
    nextTick(() => {
      innerWidth.value = __el.value.parentNode.clientWidth
      innerHeight.value = __el.value.parentNode.clientHeight
      rowsStats.width = 300
      rowsStats.height = innerHeight.value - scrollH.height
      rowsHeaderStats.width = rowsStats.width
      rowsContentStats.width = rowsStats.width
      rowsContentStats.height = rowsStats.height - rowsHeaderStats.height

      gridStats.height = innerHeight.value - scrollH.height

      scrollV.height = gridStats.height - gridHeaderStats.height
      scrollV.visible = layoutHeight.value > rowsContentStats.height

      if (scrollV.visible) {
        gridStats.width = innerWidth.value - rowsStats.width - scrollV.width
        gridStats.cellWidth = (innerWidth.value - rowsStats.width - scrollV.width) / 7
      } else {
        gridStats.width = innerWidth.value - rowsStats.width
        gridStats.cellWidth = (innerWidth.value - rowsStats.width) / 7
      }
      gridHeaderStats.width = gridStats.width
      gridContentStats.width = gridStats.width
      gridContentStats.height = gridStats.height - gridHeaderStats.height

      scrollH.width = innerWidth.value - (scrollV.visible ? scrollV.width : 0)
      scrollH.visible = layoutWidth.value > gridStats.width

      resolve(0)
    })
  })
}

const setComponentSizeDebounce = $lodash.debounce(setComponentSize, 500)

const onScroll = (scroller: string, e: any) => {
  const _target = e.target

  switch (scroller) {
  case 'h':
    if (!gridContent.value) {
      return
    }
    scrollH.left = gridContent.value.scrollLeft = gridContent.value.scrollWidth * (_target.scrollLeft / _target.scrollWidth)

    break
  case 'v':
    if (!gridContent.value) {
      return
    }
    scrollV.top = layoutHeight.value * (_target.scrollTop / _target.scrollHeight)
    if (!headerContent.value) {
      return
    }
    rowOffset.value = 0
    if (scrollV.top >= vScroll.value.scrollHeight - vScroll.value.clientHeight - (scrollH.visible ? scrollH.height : 0)) {
      rowOffset.value = -scrollH.height
    }
    viewport.rowIndex = parseInt((scrollV.top / gridStats.cellHeight).toString())
    break
  }
}
const rerouteScroll = (e: any) => {
  hScroll.value.scrollLeft += e.deltaX
  vScroll.value.scrollTop += e.deltaY
}

const checkHeaderVisible = (header: any, day: number) => {
  const temp = scrollH.left / gridStats.cellWidth
  return temp - 1 < day && temp + maxCellCount.value + 1 > day
}

const deleteItem = (id: any) => {
  const eIndex = cItems.value.findIndex((i: any) => i.id === id)
  if (eIndex >= 0) {
    cItems.value.splice(eIndex, 1)
  }
}

const checkedList = ref<any[]>([])
const checkItem = (id: string, val: boolean) => {
  if (val) {
    const exists = checkedList.value.find((c: string) => c === id)
    if (!exists) {
      checkedList.value.push(id)
    }
  } else {
    const existsIndex = checkedList.value.findIndex((c: string) => c === id)
    if (existsIndex >= 0) {
      checkedList.value.splice(existsIndex, 1)
    }
  }
}

const handleRowItemClick = (row: any, index: number, e: any) => {
  const target = e.target.closest('.hts-gantt__rows_item')
  if (target) {
    target.querySelector('input').click()
  }
}

const onChange = (item: any, e: any) => {
  emits('selection-change', item.id, e.target.checked)
}

const toggleSelection = (visible?: boolean) => {
  if (visible !== undefined) {
    selectionVisible.value = !!visible
  } else {
    selectionVisible.value = !selectionVisible.value
  }
}

const clearSelection = () => {
  document.querySelectorAll('input[name=row-group]').forEach((d: any) => {
    d.checked = false
  })
}

const onBarClick = (item: any, index: number, e: any) => {
  emits('bar-click', item, index, e)
}

defineExpose({ refresh, refreshRows, refreshItems, toggleSelection, clearSelection, onResize })
</script>
<style lang="scss">
.hts-gantt {
  &__ {
    &grid_data_bg {
      background-image: url('data:image/png;base64,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');
    }

    &grid_data_bg_item,
    &rows_item {
      @apply hover:bg-gray-200/50;
    }
  }
}
</style>
